import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Alert from "@/components/ui/alert/Alert";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { apiFetch } from "@/service/api";
import DatePicker from "@/components/Usados/date-picker";

declare global {
  interface Window {
    AuraSurveyCreator: any;
  }
}

type FormState = {
  nombre: string;
  descripcion: string;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  ficha_tecnica: string;
  codigo: string;
  tipo_estudio: string;
  fuente_financiacion: string;
  poblacion_objetivo: string;
  cobertura_geografica: string;
  cantidad: number;
  margen_error: string;
  metodo_recoleccion: string;
  documento: string;
  plantilla: string;
};

const INITIAL_FORM: FormState = {
  nombre: "",
  descripcion: "",
  fecha_inicio: null,
  fecha_fin: null,
  ficha_tecnica: "",
  codigo: "",
  tipo_estudio: "",
  fuente_financiacion: "",
  poblacion_objetivo: "",
  cobertura_geografica: "",
  cantidad: 0,
  margen_error: "",
  metodo_recoleccion: "",
  documento: "",
  plantilla: "",
};

function toInputDate(value: string | null | undefined): string {
  if (!value) return "";
  return value.slice(0, 10);
}

function dateFromInputDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function dateToInputDate(date: Date | undefined): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function tieneOpcionales(data: FormState) {
  if (data.fecha_inicio) return true;
  if (data.fecha_fin) return true;
  if (data.tipo_estudio?.trim()) return true;
  if (data.metodo_recoleccion?.trim()) return true;
  if (data.fuente_financiacion?.trim()) return true;
  if (data.poblacion_objetivo?.trim()) return true;
  if (data.cobertura_geografica?.trim()) return true;
  if (data.ficha_tecnica?.trim()) return true;
  if (data.margen_error?.trim()) return true;
  if (data.cantidad && Number(data.cantidad) !== 0) return true;
  return false;
}

export default function UpdateSurvey() {
  const { id } = useParams();
  const navigate = useNavigate();

  const creatorRef = useRef<HTMLDivElement>(null);
  const creatorInstanceRef = useRef<any>(null);
  const formDataRef = useRef<FormState>(INITIAL_FORM);

  const [formData, setFormData] = useState<FormState>(INITIAL_FORM);
  const [surveySchema, setSurveySchema] = useState<any>(null);
  const [openOpcionales, setOpenOpcionales] = useState(false);

  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [documentName, setDocumentName] = useState<string | null>(null);

  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState<string | null>(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setFetching(true);
        setError(null);

        const response = await apiFetch(`survey/find-one/${id}`);

        if (!response.ok) {
          throw new Error("No se pudo cargar la encuesta");
        }

        const data = await response.json();

        const newFormData: FormState = {
          nombre: data.nombre || "",
          descripcion: data.descripcion || "",
          fecha_inicio: toInputDate(data.fecha_inicio),
          fecha_fin: toInputDate(data.fecha_fin),
          ficha_tecnica: data.ficha_tecnica || "",
          codigo: data.codigo || "",
          tipo_estudio: data.tipo_estudio || "",
          fuente_financiacion: data.fuente_financiacion || "",
          poblacion_objetivo: data.poblacion_objetivo || "",
          cobertura_geografica: data.cobertura_geografica || "",
          cantidad: data.cantidad ?? 0,
          margen_error: data.margen_error || "",
          metodo_recoleccion: data.metodo_recoleccion || "",
          documento: data.documento || "",
          plantilla: data.plantilla || data.template || "",
        };

        setFormData(newFormData);
        if (newFormData.documento) {
          setDocumentName(newFormData.documento.split("/").pop() || null);
        }
        if (newFormData.plantilla) {
          setTemplateName(newFormData.plantilla.split("/").pop() || null);
        }

        if (tieneOpcionales(newFormData)) {
          setOpenOpcionales(true);
        }

        setSurveySchema(
          data.survey || {
            title: data.nombre || "",
            description: data.descripcion || "",
            pages: [{ name: "page1", title: "Tu opinion", elements: [] }],
          },
        );
      } catch (err: any) {
        setError(err.message || "Error al cargar la encuesta");
      } finally {
        setFetching(false);
      }
    };

    if (id) fetchSurvey();
  }, [id]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "cantidad" ? Number(value) : value,
    }));
  };

  const persistField = async (field: string, value: string) => {
    try {
      await apiFetch(`survey/update/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ [field]: value }),
      });
    } catch (err) {
      console.error(`Error guardando ${field}`, err);
    }
  };

  // DOCUMENTO (info)
  const handleOpenDocument = async () => {
    try {
      if (!formData.documento) return;
      const filename = formData.documento.split("/").pop();
      if (!filename) return;
      const response = await apiFetch(`files/survey-document/${filename}`);
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, "_blank");
      setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    } catch (err) {
      setError("No se pudo abrir el documento");
    }
  };

  const handleDocumentChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res: any = await apiFetch(`files/upload/survey-document`, {
        method: "POST",
        body: form as any,
      });
      let data = res;
      if (res && typeof res.ok !== "undefined") {
        if (!res.ok) throw new Error("Error subiendo documento");
        data = await res.json();
      }
      setFormData((p) => ({ ...p, documento: data.filePath }));
      setDocumentName(data.file);
      await persistField("documento", data.filePath);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || "Error al subir el documento");
    } finally {
      setUploadingDoc(false);
      e.target.value = "";
    }
  };

  const handleRemoveDocument = async () => {
    const previousPath = formData.documento;
    setFormData((p) => ({ ...p, documento: "" }));
    setDocumentName(null);
    await persistField("documento", "");
    if (previousPath) {
      try {
        await apiFetch(`files/delete`, {
          method: "POST",
          body: JSON.stringify({ filePath: previousPath }),
        });
      } catch {}
    }
  };

  // PLANTILLA (word para docx)
  const handleOpenTemplate = async () => {
    try {
      if (!formData.plantilla) return;
      const filename = formData.plantilla.split("/").pop();
      if (!filename) return;
      const response = await apiFetch(`files/templates/${filename}`);
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const fileURL = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileURL;
      a.download = templateName || filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
    } catch (err) {
      setError("No se pudo abrir la plantilla");
    }
  };

  const handleTemplateChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTemplate(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res: any = await apiFetch(`files/upload/template`, {
        method: "POST",
        body: form as any,
      });
      let data = res;
      if (res && typeof res.ok !== "undefined") {
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.message || "Error subiendo plantilla");
        }
        data = await res.json();
      }
      setFormData((p) => ({ ...p, plantilla: data.filePath }));
      setTemplateName(data.file);
      await persistField("plantilla", data.filePath);
      await persistField("template", data.filePath);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || "Error al subir la plantilla");
    } finally {
      setUploadingTemplate(false);
      e.target.value = "";
    }
  };

  const handleRemoveTemplate = async () => {
    const previousPath = formData.plantilla;
    setFormData((p) => ({ ...p, plantilla: "" }));
    setTemplateName(null);
    await persistField("plantilla", "");
    await persistField("template", "");
    if (previousPath) {
      try {
        await apiFetch(`files/delete`, {
          method: "POST",
          body: JSON.stringify({ filePath: previousPath }),
        });
      } catch {}
    }
  };

  useEffect(() => {
    if (fetching) return;
    if (!surveySchema) return;
    if (!creatorRef.current) return;

    const loadCss = (href: string) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    };

    loadCss("/libs/aura-survey/css/aura-survey.css");
    loadCss("/libs/aura-survey/css/aura-survey-creator.css");

    const load = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${src}"]`,
        );
        if (existing) {
          if (existing.dataset.loaded) {
            resolve();
            return;
          }
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () =>
            reject(new Error(`No se encontró ${src}`)),
          );
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => {
          script.dataset.loaded = "true";
          resolve();
        };
        script.onerror = () => reject(new Error(`No se encontró ${src}`));
        document.body.appendChild(script);
      });

    let creator: any;

    Promise.all([
      load("/libs/aura-survey/js/aura-survey.js"),
      load("/libs/aura-survey/js/aura-survey-creator.js"),
    ])
      .then(() => {
        if (!creatorRef.current) return;
        localStorage.removeItem("aura-demo-draft");
        localStorage.removeItem("aura-survey-draft");

        const initialSchema = {
          ...surveySchema,
          title: formDataRef.current.nombre || surveySchema.title || "",
          description:
            formDataRef.current.descripcion || surveySchema.description || "",
        };

        creator = new window.AuraSurveyCreator(creatorRef.current, {
          schema: initialSchema,
          storageKey: undefined,
          onSave: async (finalSchema: any) => {
            if (!formDataRef.current?.nombre?.trim()) {
              setError("El nombre es obligatorio");
              return;
            }
            if (!formDataRef.current?.codigo?.trim()) {
              setError("El código es obligatorio");
              return;
            }

            finalSchema.title = formDataRef.current.nombre;
            finalSchema.description = formDataRef.current.descripcion;

            try {
              setLoading(true);
              setError(null);
              setSuccess(false);
              const cleanPayload = {
                ...formDataRef.current,
                fecha_inicio: formDataRef.current.fecha_inicio || null,
                fecha_fin: formDataRef.current.fecha_fin || null,
              };
              const payload = { survey: finalSchema, ...cleanPayload };
              payload.survey.title = cleanPayload.nombre;
              payload.survey.description = cleanPayload.descripcion;

              const response = await apiFetch(`survey/update/${id}`, {
                method: "PATCH",
                body: JSON.stringify(payload),
              });

              if (
                response &&
                typeof response.ok !== "undefined" &&
                !response.ok
              ) {
                const data = await response.json().catch(() => null);
                const msg = Array.isArray(data?.message)
                  ? data.message.join(", ")
                  : data?.message;
                throw new Error(msg || `Error ${response.status}`);
              }

              setSuccess(true);
            } catch (err: any) {
              setError(err.message || "Error al actualizar");
            } finally {
              setLoading(false);
            }
          },
        });

        creatorInstanceRef.current = creator;
      })
      .catch((err) => setError(err.message));

    return () => {
      creator?.destroy?.();
      creatorInstanceRef.current = null;
    };
  }, [fetching, surveySchema]);

  useEffect(() => {
    const creator: any = creatorInstanceRef.current;
    if (!creator || !creator.schema) return;
    creator.schema.title = formData.nombre;
    creator.schema.description = formData.descripcion;
    if (creator.root) {
      const titleInput = creator.root.querySelector(
        '[data-field="schema-title"]',
      ) as HTMLInputElement;
      if (titleInput && document.activeElement !== titleInput)
        titleInput.value = formData.nombre;
      const h1 = creator.root.querySelector(
        ".creator-canvas-heading h1",
      ) as HTMLElement;
      if (h1) h1.textContent = formData.nombre || "Nueva encuesta";
    }
    creator.persist?.();
    creator.emit?.("change", creator.getSchema());
  }, [formData.nombre, formData.descripcion]);

  if (fetching) {
    return (
      <div className="flex items-center justify-center min-h-">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500" />
      </div>
    );
  }

  const inputClass =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";
  const labelClass =
    "mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400";

  return (
    <div>
      <PageBreadcrumb pageTitle="Actualizar Encuesta" />
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12 space-y-8">
        <h3 className="font-semibold text-gray-800 text-center text-xl dark:text-white/90">
          Actualizar Encuesta #{id}
        </h3>
        {error && <Alert variant="error" title="Error" message={error} />}
        {success && (
          <Alert
            variant="success"
            title="Éxito"
            message="Encuesta actualizada correctamente"
          />
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className={labelClass}>Nombre *</label>
            <input
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Código *</label>
            <input
              name="codigo"
              value={formData.codigo}
              onChange={handleInputChange}
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Descripción</label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              rows={2}
              className={inputClass + " h-auto"}
            />
          </div>

          <div>
            <label className={labelClass}>
              Documento de la encuesta (PDF informativo)
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <label
                className={`flex h-11 cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 px-6 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-white/70 ${
                  uploadingDoc ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleDocumentChange}
                />
                {uploadingDoc
                  ? "Subiendo..."
                  : formData.documento
                  ? "Cambiar documento"
                  : "Seleccionar documento"}
              </label>
              {formData.documento && (
                <div className="flex items-center gap-2 text-sm">
                  <button
                    type="button"
                    onClick={handleOpenDocument}
                    className="max-w- truncate text-left text-sm font-medium text-brand-500 underline hover:text-brand-600"
                  >
                    {documentName || formData.documento.split("/").pop()}
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveDocument}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    Quitar
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>
              Plantilla Word (DOCX para generar)
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <label
                className={`flex h-11 cursor-pointer items-center justify-center rounded-lg border border-dashed border-purple-300 bg-purple-50 px-6 text-sm font-medium text-purple-700 hover:bg-purple-100 dark:border-purple-700 dark:text-white/70 ${
                  uploadingTemplate ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <input
                  type="file"
                  className="hidden"
                  accept=".docx"
                  onChange={handleTemplateChange}
                />
                {uploadingTemplate
                  ? "Subiendo..."
                  : formData.plantilla
                  ? "Cambiar plantilla"
                  : "Seleccionar plantilla .docx"}
              </label>
              {formData.plantilla && (
                <div className="flex items-center gap-2 text-sm">
                  <button
                    type="button"
                    onClick={handleOpenTemplate}
                    className="max-w- truncate text-left text-sm font-medium text-purple-600 underline hover:text-purple-700"
                  >
                    {templateName || formData.plantilla.split("/").pop()}
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveTemplate}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    Quitar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setOpenOpcionales(!openOpcionales)}
            className="flex w-full items-center justify-between bg-gray-50 px-5 py-4 text-left dark:bg-white/[0.03]"
          >
            <span className="text-sm font-semibold text-gray-800 dark:text-white/90">
              Campos opcionales (adicionales)
            </span>
            <span className="text-gray-500">{openOpcionales ? "▲" : "▼"}</span>
          </button>
          {openOpcionales && (
            <div className="grid grid-cols-1 gap-6 border-t border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-transparent md:grid-cols-2 xl:p-6">
              <div>
                <label className={labelClass}>Fecha inicio</label>
                <DatePicker
                  id="fecha_inicio"
                  placeholder="Selecciona una fecha"
                  defaultDate={dateFromInputDate(formData.fecha_inicio)}
                  onChange={(d: Date[]) =>
                    setFormData((p) => ({
                      ...p,
                      fecha_inicio: dateToInputDate(d[0]),
                    }))
                  }
                />
              </div>
              <div>
                <label className={labelClass}>Fecha fin</label>
                <DatePicker
                  id="fecha_fin"
                  placeholder="Selecciona una fecha"
                  defaultDate={dateFromInputDate(formData.fecha_fin)}
                  onChange={(d: Date[]) =>
                    setFormData((p) => ({
                      ...p,
                      fecha_fin: dateToInputDate(d[0]),
                    }))
                  }
                />
              </div>
              <div>
                <label className={labelClass}>Tipo estudio</label>
                <input
                  name="tipo_estudio"
                  value={formData.tipo_estudio}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Método recolección</label>
                <input
                  name="metodo_recoleccion"
                  value={formData.metodo_recoleccion}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Fuente financiación</label>
                <input
                  name="fuente_financiacion"
                  value={formData.fuente_financiacion}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Población objetivo</label>
                <input
                  name="poblacion_objetivo"
                  value={formData.poblacion_objetivo}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Cobertura geográfica</label>
                <input
                  name="cobertura_geografica"
                  value={formData.cobertura_geografica}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Ficha técnica</label>
                <input
                  name="ficha_tecnica"
                  value={formData.ficha_tecnica}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Cantidad</label>
                <input
                  type="number"
                  name="cantidad"
                  value={formData.cantidad}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Margen error</label>
                <input
                  name="margen_error"
                  value={formData.margen_error}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className="mb-4 text-lg font-semibold dark:text-white">
            Constructor {loading ? "(Guardando...)" : ""}
          </h4>
          <div
            ref={creatorRef}
            className="min-h- w-full rounded-lg border border-gray-200 dark:border-gray-700"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-11 rounded-lg border px-6 text-sm dark:border-gray-700 dark:text-white"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
